import staffApi from "@/api/staff";
import { i18n } from "@/plugins/i18n";

const state = {
  isLoading: false,
  isSubmitting: false,
  member: {},
  data: {},
  errors: [],
  isResending: false,
  isResetting: false,
  isUpdatingAvatar: false,
  isActivating: false,
};

const mutations = {
  resetErrors(state) {
    state.errors = [];
  },
  getStaffStart(state) {
    state.isLoading = true;
    state.errors = [];
  },
  getStaffSuccess(state, payload) {
    state.isLoading = false;
    state.errors = [];
    state.data = payload;
  },
  getStaffFailed(state, payload) {
    state.isLoading = false;
    state.errors = payload;
  },

  createMemberStart(state) {
    state.isSubmitting = true;
    state.errors = [];
  },
  createMemberSuccess(state) {
    state.isSubmitting = false;
  },
  createMemberFailed(state, payload) {
    state.isSubmitting = false;
    state.errors = payload;
  },

  getMemberStart(state) {
    state.isLoading = true;
    state.errors = [];
  },
  getMemberSuccess(state, payload) {
    state.isLoading = false;
    state.member = payload;
  },
  getMemberFailed(state, payload) {
    state.isLoading = false;
    state.errors = payload;
  },

  updateMemberStart(state) {
    state.isSubmitting = true;
    state.errors = [];
  },
  updateMemberSuccess(state, payload) {
    state.isSubmitting = false;
    state.member = payload;
  },
  updateMemberFailed(state, payload) {
    state.isSubmitting = false;
    state.errors = payload;
  },

  resendEmailStart(state) {
    state.isResending = true;
    state.errors = [];
  },
  resendEmailSuccess(state, payload) {
    state.isResending = false;
    state.errors = [];
    state.member = payload;
  },
  resendEmailFailed(state, payload) {
    state.isResending = false;
    state.errors = payload;
  },

  updateAvatarStart(state) {
    state.isUpdatingAvatar = true;
    state.errors = [];
  },
  updateAvatarSuccess(state, payload) {
    state.isUpdatingAvatar = false;
    state.member = payload;
  },
  updateAvatarFailed(state, payload) {
    state.isUpdatingAvatar = false;
    state.errors = payload;
  },

  resetPasswordStart(state) {
    state.isResetting = true;
    state.errors = [];
  },
  resetPasswordSuccess(state) {
    state.isResetting = false;
    state.errors = [];
  },
  resetPasswordFailed(state, payload) {
    state.isResetting = false;
    state.errors = payload;
  },

  changeStatusStart(state) {
    state.isActivating = true;
    state.errors = [];
  },
  changeStatusSuccess(state, payload) {
    state.member = payload;
    state.isActivating = false;
    state.errors = [];
  },
  changeStatusFailed(state, payload) {
    state.isActivating = false;
    state.errors = payload;
  },
};

const actions = {
  async getStaff(context, payload) {
    try {
      context.commit("getStaffStart");

      const res = await staffApi.getStaff(payload);
      context.commit("getStaffSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("getStaffFailed", e.response.data.error);
      return false;
    }
  },

  async createMember(context, payload) {
    try {
      context.commit("createMemberStart");

      await staffApi.createMember(payload);
      context.commit("createMemberSuccess");
      return true;
    } catch (e) {
      context.commit("createMemberFailed", e.response.data.error);
      return false;
    }
  },

  async getMember(context, payload) {
    try {
      context.commit("getMemberStart");

      const res = await staffApi.getMember(payload);
      context.commit("getMemberSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("getMemberFailed", e.response.data.error);
      return false;
    }
  },

  async updateMember(context, { id, data }) {
    try {
      context.commit("updateMemberStart");

      const res = await staffApi.updateMember(id, data);
      context.commit("updateMemberSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("updateMemberFailed", e.response.data.error);
      return false;
    }
  },

  async staffResendEmail(context, payload) {
    try {
      context.commit("resendEmailStart");

      const res = await staffApi.resendEmail(payload);
      context.commit("resendEmailSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("resendEmailFailed", e.response.data.error);
      return false;
    }
  },

  async staffUpdateAvatar(context, { id, data }) {
    try {
      context.commit("updateAvatarStart");
      const res = await staffApi.updateAvatar(id, data);
      context.commit("updateAvatarSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("updateAvatarFailed", e.response.data.error);
      return false;
    }
  },

  async staffResetPassword(context, payload) {
    try {
      context.commit("resetPasswordStart");

      await staffApi.resetPassword(payload);
      context.commit("resetPasswordSuccess");
      return true;
    } catch (e) {
      context.commit("resetPasswordFailed", e.response.data.error);
      return false;
    }
  },

  async suspendMember(context, payload) {
    try {
      context.commit("changeStatusStart");

      const res = await staffApi.suspendMember(payload);
      context.commit("changeStatusSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("changeStatusFailed", e.response.data.error);
      return false;
    }
  },

  async restoreMember(context, payload) {
    try {
      context.commit("changeStatusStart");

      const res = await staffApi.restoreMember(payload);
      context.commit("changeStatusSuccess", res.data);
      return true;
    } catch (e) {
      context.commit("changeStatusFailed", e.response.data.error);
      return false;
    }
  },
};

const getters = {
  staffList(state) {
    return state.data?.result || [];
  },
  staffMemberFullName(state) {
    const { first_name, last_name } = state.member;
    return `${first_name} ${last_name}`;
  },
  staffIsEmailConfirmed(state) {
    const confirmation = state.member.is_email_confirmed;
    return confirmation === undefined ? true : confirmation;
  },
  staffFirstNameErrors({ errors }) {
    const result = [];
    errors.find((i) => i == "first_name__required") &&
      result.push(i18n.t("errors.firstNameRequired"));
    errors.find((i) => i == "first_name__invalid") &&
      result.push(i18n.t("errors.firstNameInvalid"));
    return result;
  },
  staffLastNameErrors({ errors }) {
    const result = [];
    errors.find((i) => i == "last_name__required") &&
      result.push(i18n.t("errors.lastNameRequired"));
    errors.find((i) => i == "last_name__invalid") &&
      result.push(i18n.t("errors.lastNameInvalid"));
    return result;
  },
  staffEmailErrors({ errors }) {
    const result = [];
    errors.find((i) => i == "email__required") &&
      result.push(i18n.t("errors.emailRequired"));
    errors.find((i) => i == "email__invalid") &&
      result.push(i18n.t("errors.emailInvalid"));
    errors.find((i) => i == "email__exists") &&
      result.push(i18n.t("errors.emailExists"));
    return result;
  },
  staffRoleErrors({ errors }) {
    const result = [];
    errors.find((i) => i == "role__required") &&
      result.push(i18n.t("errors.roleRequired"));
    errors.find((i) => i == "role__invalid") &&
      result.push(i18n.t("errors.roleInvalid"));
    return result;
  },
  staffIsSuspended(state) {
    return state.member.is_suspended;
  },
};

export default {
  state,
  mutations,
  actions,
  getters,
};
